import { all, call, put, takeEvery } from 'redux-saga/effects'
import { t } from 'ttag'
import { addNotification } from '../../../common/notifications/NotificationActions'
import { INotification } from '../../../common/notifications/NotificationReducers'
import api from '../../../services/api.services'
import { getErrorMsg } from '../../../common/helpers/translations'
import {
  getCompanySignUpCodes,
  getCompanySignUpCodesSuccess,
  getCompanySignUpCodesFail,
  createCompanySignUpCode,
  createCompanySignUpCodeSuccess,
  createCompanySignUpCodeFail,
  updateCompanySignUpCode,
  updateCompanySignUpCodeSuccess,
  updateCompanySignUpCodeFail,
  deleteCompanySignUpCode,
  deleteCompanySignUpCodeSuccess,
  deleteCompanySignUpCodeFail,
} from './SignUpCodeAdminActions'

/**
 * Saga: fetch all sign-up codes for a company.
 */
function* sagaGetCompanySignUpCodes({ payload }: any) {
  try {
    const { companyId } = payload
    const response: any = yield call(api.users.getCompanySignUpCodes, { companyId })
    yield put({ type: String(getCompanySignUpCodesSuccess), payload: response })
  } catch (e: any) {
    yield put({ type: String(getCompanySignUpCodesFail) })
    const notification: INotification = {
      type: 'warning',
      content: getErrorMsg('getCompanySignUpCodes'),
    }
    yield put({ type: String(addNotification), payload: notification })
  }
}

/**
 * Saga: create a new sign-up code.
 * Reloads the code list on success.
 */
function* sagaCreateCompanySignUpCode({ payload }: any) {
  try {
    yield call(api.users.createCompanySignUpCode, payload)
    yield put({ type: String(createCompanySignUpCodeSuccess) })

    const notification: INotification = {
      type: 'success',
      content: t`Sign-up code created successfully.`,
    }
    yield put({ type: String(addNotification), payload: notification })

    // Reload codes to get the latest list
    yield put({ type: String(getCompanySignUpCodes), payload: { companyId: payload.companyId } })
  } catch (e: any) {
    yield put({ type: String(createCompanySignUpCodeFail) })
    const notification: INotification = {
      type: 'warning',
      content:
        e.message && e.message !== 'No message has been provided by the server'
          ? e.message
          : getErrorMsg('createCompanySignUpCode'),
    }
    yield put({ type: String(addNotification), payload: notification })
  }
}

/**
 * Saga: update an existing sign-up code.
 * Reloads the code list on success.
 */
function* sagaUpdateCompanySignUpCode({ payload }: any) {
  try {
    yield call(api.users.updateCompanySignUpCode, payload)
    yield put({ type: String(updateCompanySignUpCodeSuccess) })

    const notification: INotification = {
      type: 'success',
      content: t`Sign-up code updated successfully.`,
    }
    yield put({ type: String(addNotification), payload: notification })

    // Reload codes
    yield put({ type: String(getCompanySignUpCodes), payload: { companyId: payload.companyId } })
  } catch (e: any) {
    yield put({ type: String(updateCompanySignUpCodeFail) })
    const notification: INotification = {
      type: 'warning',
      content:
        e.message && e.message !== 'No message has been provided by the server'
          ? e.message
          : getErrorMsg('updateCompanySignUpCode'),
    }
    yield put({ type: String(addNotification), payload: notification })
  }
}

/**
 * Saga: delete a sign-up code.
 * Reloads the code list on success.
 */
function* sagaDeleteCompanySignUpCode({ payload }: any) {
  try {
    yield call(api.users.deleteCompanySignUpCode, payload)
    yield put({ type: String(deleteCompanySignUpCodeSuccess) })

    const notification: INotification = {
      type: 'success',
      content: t`Sign-up code deleted.`,
    }
    yield put({ type: String(addNotification), payload: notification })

    // Reload codes
    yield put({ type: String(getCompanySignUpCodes), payload: { companyId: payload.companyId } })
  } catch (e: any) {
    yield put({ type: String(deleteCompanySignUpCodeFail) })
    const notification: INotification = {
      type: 'warning',
      content:
        e.message && e.message !== 'No message has been provided by the server'
          ? e.message
          : getErrorMsg('deleteCompanySignUpCode'),
    }
    yield put({ type: String(addNotification), payload: notification })
  }
}

export function* signUpCodeAdminSagas() {
  yield all([
    takeEvery(String(getCompanySignUpCodes), sagaGetCompanySignUpCodes),
    takeEvery(String(createCompanySignUpCode), sagaCreateCompanySignUpCode),
    takeEvery(String(updateCompanySignUpCode), sagaUpdateCompanySignUpCode),
    takeEvery(String(deleteCompanySignUpCode), sagaDeleteCompanySignUpCode),
  ])
}
